import Swiper, { Mousewheel } from "swiper";
import 'swiper/swiper-bundle.css';
import { renderPlaceholderContainers } from "../common/placeholder-loader";
import { attachProductItemEvents, getProductItemBody, renderVideos } from "../common/product-item";
import { fetchProductsById } from "../services/products";
import { createLazyAction } from "../utils/lazy-action";
import { waitObjectProperty } from "../utils/performance";

Swiper.use([Mousewheel]);

class QuickShopSimplified {
  constructor(id) {
    this.id = id;
    this.state = {
      elements: {},
      productsIds: [],
      swiper: null,
      renderedProductsState: {},
      products: [],
      scrollInterval: {
        slidePrev: false,
        slideNext: false,
      },
    };

    this.init()
  }

  init() {
    this.cacheElements(),
    renderPlaceholderContainers(this.state.elements.quickShopLoader, 4, 'product'),
    this.state.elements.quickShopContainer && this.renderModule()()
  }

  cacheElements () {
    const quickShopContainer = document.querySelector(`#quick-shop-${this.id}.quick-shop`);
    if (!quickShopContainer) return;
    const quickShopLoader = quickShopContainer.querySelector('.quick-shop__loader');
    const productsContainer = quickShopContainer.querySelector('.quick-shop__body');
    this.state.elements = {
      ...this.state.elements,
      quickShopContainer,
      productsContainer,
      quickShopLoader,
      swiperNext: quickShopContainer.querySelector('.quick-shop__body--next'),
      swiperPrev: quickShopContainer.querySelector('.quick-shop__body--prev'),
    };
    this.state.productsIds = window.quickShopSettings[this.id]?.products.map(product => product.id)
  }

  renderModule () {
    return createLazyAction(
      this.state.elements.quickShopContainer,
      async () => (
        this.attachListeners(),
        await this.fetchProducts(),
        this.initSwiper(),
        await this.renderProducts()
      ),
      '.quick-shop__loader',
    )
  }

  async fetchProducts () {
    if (!this.state.productsIds.length) return;
    const productsFromStorefront = await fetchProductsById(this.state.productsIds);
    this.state.products = this.state.productsIds.map((id, index) => {
      let productObject = productsFromStorefront[index];
      if (productObject?.id != id) {
        productObject = productsFromStorefront.find(product => product.id == id)
      }
      return productObject;
    })
    this.state.products.sort((a, b) => {
      const indexA = this.state.productsIds.indexOf(a.id);
      const indexB = this.state.productsIds.indexOf(b.id);
      return indexA - indexB;
    });
    this.state.products = this.attachImages(window.quickShopSettings[this.id]?.products, this.state.products)
  };

  attachImages(quickShopProducts, storeFrontProducts) {
    if (!Array.isArray(quickShopProducts) || !Array.isArray(storeFrontProducts)) return storeFrontProducts;
    return quickShopProducts.map((product) => {
      if (!product?.id) {
        console.warn("Skipping product with missing id:", product);
        return {};
      }
      let currentProduct = storeFrontProducts.find(storefrontProduct => storefrontProduct?.id === product.id);
      if (!currentProduct) {
        console.warn(`No matching storeFrontProduct found for product ID: ${product.id}`);
        currentProduct = {};
      }
      if (product.blockType === 'image') {
        return {
          ...currentProduct,
          blockType: "image",
          userImage: product.userImage,
          shopEnabled: product.shopEnabled,
          shopText: product.shopText,
          shopProduct: product.shopProduct,
          shopColor: product.shopColor,
          url: product.userUrl,
          title: product.userTitle,
          userImageWidth: product.userImageWidth,
          userImageHeight: product.userImageHeight
        };
      }
      return currentProduct;
    }).filter(product => Object.keys(product).length > 0);
  }

  autoPlaySliderOnHover (direction) {
    const scroll = () => {
      if (this.state.currentViewProduct) return
      this.state.swiper[direction](1500)
    }
  
    scroll()
    this.stopScroll(direction)
    this.state.scrollInterval[direction] = setInterval(scroll, 0)
  }

  stopScroll = function(direction) {
    clearInterval(this.state.scrollInterval[direction])
  }

  onAfterSwiperInit() {
    const {swiperPrev, swiperNext} = this.state.elements

    swiperPrev.addEventListener(
      'mouseover', this.autoPlaySliderOnHover.bind(this,"slidePrev")
    )
    swiperNext.addEventListener(
      'mouseover', this.autoPlaySliderOnHover.bind(this, "slideNext")
    )
    swiperPrev.addEventListener(
      'mouseout', this.stopScroll.bind(this, "slidePrev")
    )
    swiperNext.addEventListener(
      'mouseout', this.stopScroll.bind(this, "slideNext")
    )
  }

  initSwiper () {
    this.state.swiper = new Swiper(`#quick-shop-${this.id} .quick-shop__body`, {
      breakpoints: {
        769: {
          slidesPerView: 4.2,
        },
      },
      spaceBetween: 5,
      slidesPerView: 2.2,
      loop: true,
      loopAdditionalSlides: this.state.productsIds.length * 4,
      freeMode: false,
      centeredSlides: false,
      mousewheel: {
        forceToAxis: true,
        sensitivity: 1,
        releaseOnEdges: true,
        thresholdDelta: 10,
        thresholdTime: 200,
      },
      resistance: false,
      // on: {
      //   afterInit: this.onAfterSwiperInit.bind(this)
      // }
    });
  };

  async renderProducts () {
    if (!this.state.products.length) return;
    try{
      await waitObjectProperty("GLBE_PARAMS", window)
      if (GLBE_PARAMS.currencyCode !== "USD") {
        await waitObjectProperty("PriceInfo", window.GLBE_PARAMS)
      } 
    }catch(e) {
      console.error(e)
    }
    const productItemTemplates = this.state.products.map((product, index) => {
      const newProduct = {...product}
      if (GLBE_PARAMS.currencyCode !== "USD") {
        if (newProduct.compareAtPrice) {
          newProduct.compareAtPrice = glbeApp.modules.priceInfo.convertPrice(newProduct.compareAtPrice)
        }
        newProduct.price = glbeApp.modules.priceInfo.convertPrice(newProduct.price)
      }
      if (newProduct.blockType === 'image') {
        return `
          <div class="swiper-slide image-slide">
            <div class="image-wrapper">
              <img src="${newProduct.userImage}" alt="${newProduct.title || "Quick-shop item media" }" width="${newProduct.userImageWidth}" height="${newProduct.userImageHeight}" />
              ${
                newProduct.url ? 
                `<a class=image-slide--link href="${newProduct.url}" title="${newProduct.title || "Quick-shop item media" }"> </a>` :
                ''
              }
                ${newProduct.shopEnabled ? `<div data-shop-modal-open class="shopable-scroll-section_shop">
                    ${JSON.parse(newProduct.shopProduct).map((product) => {
                        return `
                          <script data-product-obj="${product.handle}" type="application/json">
                            {
                              "product": ${JSON.stringify(product)}
                            }
                          </script>
                        `
                      }).join(' ')}
                    <script data-shop-images type="application/json">
                      {
                        "images": [
                          "<img src='${newProduct.userImage}' alt='${newProduct.title || 'Quick-shop item media'}' width='${newProduct.userImageWidth}' height='${newProduct.userImageHeight}' />"
                        ]
                      }
                      </script>

                    <button
                      style="--shopable-scroll-section-shop-color: ${newProduct.shopColor}"
                      class="Button-tertiary"
                      aria-label="${newProduct.shopText}">
                      ${newProduct.shopText}
                    </button>
                  </div>` : ""}
            </div>
            ${
              newProduct.title ?
              `<div class="image-slide--footer ">
                ${
                  newProduct.url ?
                  `<a class="product-title text" href="${newProduct.url} title="${newProduct.title}">${newProduct.title}</a>`:
                  `<p class="product-title text" href="${newProduct.url}">${newProduct.title}</p>`
                }
                  
              </div>` :
              ""
            }
          </div>
        `
      }

      return `
      <div class="swiper-slide">
        ${getProductItemBody(
          newProduct,
          index,
          this.state.renderedProductsState,
          false,
          this.state.elements.productsContainer,
        )}
      </div>
    `
    });
    this.state.swiper.appendSlide([
      ...productItemTemplates,
      ...productItemTemplates,
      ...productItemTemplates,
    ]);
    attachProductItemEvents(
      this.state.renderedProductsState,
      this.state.elements.productsContainer,
    );
    renderVideos(this.state.products, this.state.renderedProductsState);
  }

  attachListeners () {
    if(Shopify.designMode) {
      window.addEventListener('shopify:section:load', (e) => {
        if(e.detail.sectionId === this.id) {
          this.init();
        }
      });
    }
  };

}

const init = () => {
  const quickShopSections = window?.quickShopSettings
  if (!quickShopSections) return

  Object.entries(quickShopSections).forEach(([k, v]) => {
    if (v.simplified) {
      new QuickShopSimplified(k)
    }
  })
}

export default init;
